// Service autonome de la case Commande x BDC (PRESTATAIRE).
// Cible les vraies routes prestataire confirmees cote core api.php :
//   - /bdc/commande/{id_commande}/...                 (catalogue, transfertAcheteur, consultation)
//   - /bdc/prestataire/signature/{commande_id}/...    (accept = signerEtAccepter, transfer = signerEtTransferer, otp)
// Remplace l'ancienne infra cassee/fuyante : useBCActions/bcService tapaient des
// routes MS /operation-ms/* depuis une commande BC. Ici, tout est presta-BC, id-based.
//
// NB chargement : la commande est chargee via commandeBCService.getById (EntityService,
// GET /bdc/commande/{id} -> {status, data} + mapping `formulaire`), cf. hooks/useCommandeData.
// Ce service-ci porte les ACTIONS presta (remplies au fil des etapes 4-6 du refactor).

const authedFetch = async <T = unknown>(path: string, options: RequestInit = {}): Promise<T> => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error("Aucun token d'authentification trouvé");

  const response = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(options.headers || {}),
    },
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => ({}))) as { message?: string };
    throw new Error(data.message || `Erreur HTTP: ${response.status}`);
  }
  return response.json().catch(() => ({})) as Promise<T>;
};

// --- ACTIONS prestataire -------------------------------------------------------
// Toutes id-based : la commande passe son id en argument, JAMAIS via window.location
// (le legacy ConsultationBC derivait l'id de l'URL : window.location.pathname.split('/')[2]
// / indexOf('bon-commande'), smell a NE PAS reproduire). Routes confirmees cote core api.php,
// toujours prefixees /bdc (le legacy tapait /commande/{id}/refuse SANS /bdc : smell evite).

// Refuser la commande (panier normal). Remplace handleRefuserCommandePrestataire legacy
// (l.2181-2212) qui tapait /commande/{id}/refuse sans prefixe : ici route /bdc confirmee.
export const refuserCommande = async (commandeId: number): Promise<unknown> =>
  authedFetch(`/bdc/commande/${commandeId}/refuse`, { method: 'POST' });

// Enregistrer (brouillon) l'offre technique du presta : PUT /bdc/commande/{id}/offre-technique.
// Route core confirmee (proxy vers bdc CommandeController@updateOffreTechnique). Le back valide
// nullable|string|max:5000 (brouillon vide autorise) et refuse (422) une fois l'offre partie ->
// le front rend deja le champ readonly a ce stade, ce chemin n'est donc pas sollicite. Body snake_case
// aligne sur la colonne `commande.offre_technique`, reponse { data: { offre_technique } }.
export const saveOffreTechnique = async (
  commandeId: number,
  offreTechnique: string,
): Promise<unknown> =>
  authedFetch(`/bdc/commande/${commandeId}/offre-technique`, {
    method: 'PUT',
    body: JSON.stringify({ offre_technique: offreTechnique }),
  });

// Attestation "documents administratifs a jour" (case OBLIGATOIRE de l'onglet Offre) :
// PUT /bdc/commande/{id}/attestation-documents. Route core dediee (proxy vers bdc
// CommandeController@updateAttestationDocuments), calquee sur offre-technique : elle pose
// attestation_documents_at = now() quand atteste=true, la remet a null quand atteste=false.
// POURQUOI une route dediee plutot qu'un flag dans le tunnel de signature : le body n'est pas relaye
// uniformement sur les 3 points d'envoi (accept relaie le body, transfert non), une route dediee est
// donc robuste et uniforme. Contrairement a offre-technique, PAS de gel de statut cote back : la case
// reste modifiable apres l'envoi (regle 6). Body { atteste: boolean } valide required|boolean cote
// back ; route ouverte au presta (roles interlocuteur/gestionnaire/signataire).
export const saveAttestationDocuments = async (
  commandeId: number,
  atteste: boolean,
): Promise<unknown> =>
  authedFetch(`/bdc/commande/${commandeId}/attestation-documents`, {
    method: 'PUT',
    body: JSON.stringify({ atteste }),
  });

// Supprimer un article du catalogue (panier inverse) : DELETE /bdc/commande/{id}/catalogue/{catalogueId}.
export const deleteCatalogueArticle = async (
  commandeId: number,
  catalogueId: string,
): Promise<unknown> =>
  authedFetch(`/bdc/commande/${commandeId}/catalogue/${catalogueId}`, { method: 'DELETE' });

// Etape 5 (Offre presta) :
//   - accept   via SignatureFlow (resourcePrefix='bdc/prestataire', .../accept   = signerEtAccepter)
//   - transfer via SignatureFlow (resourcePrefix='bdc/prestataire', .../transfer = signerEtTransferer)
//   - telechargerBonCommande -> POST /documents/signed-contrat (source 'BC')

// URLs de signature presta (passees a SignatureFlow via la prop signerUrl). Cote presta,
// operationId === commandeId === commande.id (legacy OffreBC l.2479-2482 / 2499-2502 : la
// commande passe son propre id en operationId ET commandeId). Le SignatureFlow construit son
// baseUrl /bdc/prestataire/signature/{id} ; signerUrl REMPLACE le POST final (.../signer)
// par l'endpoint metier (.../accept ou .../transfer). Routes core confirmees api.php l.974-979.
const apiBase = (): string => process.env.REACT_APP_API_URL ?? '';

// ACCEPT (panier normal : accepter le BC) -> POST /bdc/prestataire/signature/{id}/accept
export const buildAcceptSignerUrl = (commandeId: number): string =>
  `${apiBase()}/bdc/prestataire/signature/${commandeId}/accept`;

// TRANSFER (panier inverse "A chiffrer" : envoyer l'offre chiffree) -> .../{id}/transfer
export const buildTransferSignerUrl = (commandeId: number): string =>
  `${apiBase()}/bdc/prestataire/signature/${commandeId}/transfer`;

// Telecharge le bon de commande signe (reponse PDF brute) : POST /documents/signed-contrat.
// Calque l'acheteur operationBcService.telechargerBonCommande (l.79-98). CHAMP EXACT
// (core DocumentController.downloadSignedContratDocument) : pour source 'BC' le backend lit
// QUE external_id (= le NUMERO du document) + source ; external_type est valide mais NON utilise
// dans le lookup BC. external_id DOIT etre commande.operation?.numero (le presta legacy envoie
// numero.toString(), cf. OffreBC l.952). On garde external_type:'operation' par parite.
export const telechargerBonCommande = async (externalId: string | number): Promise<Blob> => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error("Aucun token d'authentification trouvé");

  const response = await fetch(`${process.env.REACT_APP_API_URL}/documents/signed-contrat`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/pdf',
    },
    body: JSON.stringify({
      external_id: String(externalId),
      external_type: 'operation',
      source: 'BC',
    }),
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => ({}))) as { message?: string };
    throw new Error(data.message || 'Erreur lors du téléchargement du bon de commande');
  }
  return response.blob();
};

// On expose deja le helper authedFetch pour ces ajouts.
export { authedFetch };
